import React from 'react';
import styled from '@emotion/styled';
import Button from '../../../components/Button';
import { useGlobalContext } from '../../state/GlobalContext';
import { useSkipOptions } from '../../state/useSkipOptions';
import ActionsMenu from '../../../components/actionsMenu';

interface SkipNextButtonProps {
  subscription: Subscription;
  subscriptionIsInDunning: boolean;
  isStubbedData?: boolean;
  handleStubDataChanges: () => void;
  handleSkip: (intervalId?: string) => void;
  t: (key: string) => string;
}

const SkipNextButton = ({
  subscription,
  subscriptionIsInDunning,
  isStubbedData,
  handleStubDataChanges,
  handleSkip,
  t,
}: SkipNextButtonProps): React.ReactElement | null => {
  const { customerPortalSettings } = useGlobalContext();
  const { skipOptions } = useSkipOptions();

  const isActive = subscription?.SubscriptionStatus === 'ACTIVE';
  const isBillingOrUpdatingOrScheduled =
    subscription?.IsBilling ||
    subscription?.IsUpdating ||
    subscription?.IsBillingScheduled;

  const hideSkipNext =
    customerPortalSettings?.hideReschedule ||
    customerPortalSettings?.hideSkipNext;

  return isActive && !subscriptionIsInDunning && !hideSkipNext ? (
    skipOptions ? (
      <SkipNextButtonDropdown
        t={t}
        isBillingOrUpdatingOrScheduled={isBillingOrUpdatingOrScheduled}
        isStubbedData={isStubbedData}
        handleStubDataChanges={handleStubDataChanges}
        handleSkip={handleSkip}
        skipOptions={skipOptions}
      />
    ) : (
      <SkipNextButtonCta
        t={t}
        isBillingOrUpdatingOrScheduled={isBillingOrUpdatingOrScheduled}
        isStubbedData={isStubbedData}
        handleStubDataChanges={handleStubDataChanges}
        handleSkip={handleSkip}
      />
    )
  ) : null;
};

const SkipNextButtonDropdown = ({
  t,
  isBillingOrUpdatingOrScheduled,
  isStubbedData,
  handleStubDataChanges,
  handleSkip,
  skipOptions,
}) => (
  <ActionsMenu
    buttonClassnames="bundle-button--skip"
    isRemovable={true}
    isEditable={true}
    menuOptions={skipOptions.map((skipOption) => ({
      type: 'skip',
      text: skipOption.label,
      action: () =>
        isStubbedData ? handleStubDataChanges() : handleSkip(skipOption.id),
    }))}
    ButtonComponent={SkipMenuButtonDropdown}
    buttonDisabled={isBillingOrUpdatingOrScheduled}
    text={t('subscription.skip_next')}
  />
);

const SkipNextButtonCta = ({
  t,
  isBillingOrUpdatingOrScheduled,
  isStubbedData,
  handleStubDataChanges,
  handleSkip,
}): React.ReactElement => (
  <CustomButton
    className="bundle-button--skip"
    outlined
    disabled={isBillingOrUpdatingOrScheduled}
    fullWidth
    onClick={() => (isStubbedData ? handleStubDataChanges() : handleSkip())}
  >
    {t('subscription.skip_next')}
  </CustomButton>
);

const CustomButton = styled(Button)`
  display: flex;
  height: auto;
  justify-content: center;
  align-items: center;
  padding: 8px;
`;

interface ButtonComponent {
  onClick?: (e: any) => void;
  text?: string;
  children?: string | React.ReactNode;
  label?: string;
  className?: string;
  disabled?: boolean;
}
const SkipMenuButtonDropdown = React.forwardRef(
  ({ onClick, children, disabled }: ButtonComponent, ref) => (
    <CustomSkipNextButton
      className="bundle-button--skip"
      onClick={onClick}
      disabled={disabled}
      outlined
      fullWidth
    >
      {children}
    </CustomSkipNextButton>
  ),
);

const CustomSkipNextButton = styled(Button)`
  display: flex;
  height: auto;
  justify-content: center;
  align-items: center;
  padding: 8px;
`;

export default SkipNextButton;
